import { FilterGroupType } from '../Components/ProductFiltering/types/FilterGroupType';
import {
  PRODUCT_FILTERING_RECEIVE,
  PRODUCT_FILTERING_SET_SORTING,
  PRODUCT_FILTERING_UPDATE_FILTERS,
  UPDATE_CATEGORY_DESCRIPTION,
} from '../constants';
import { get } from '../Services/http';
import {
  setIsLoadingProductListing,
  setProducts,
} from './ProductListing.action';

let abortController = null;

export const queryProductFiltering = (
  productFilteringState = null,
  isInitialLoad = false
) => async (dispatch, getState) => {
  try {
    productFilteringState ??= getState().productFiltering;
    const productListingState = getState().productListing;

    // Show skeleton cards before loading the new products
    if (!isInitialLoad) {
      dispatch(setIsLoadingProductListing(true));
      dispatch(
        setProducts(Array.from({ length: productListingState.pageSize }))
      );
    }

    // Cancel the last request
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    const queryString = getProductFilteringAsQueryString(
      productFilteringState,
      productListingState
    );

    const response = await get(
      `/api/productfiltering?${queryString}`,
      abortController
    );

    if (response.ok) {
      const isJson = response.headers
        .get('content-type')
        ?.includes('application/json');
      if (isJson) {
        let data = await response.json();
        if (isInitialLoad) {
          delete data.productListing;
        }

        dispatch(receiveProductFiltering(data));
      }
    }
  } catch (err) {
    // TODO Error handling, not in MOX-86
    console.log(err);
  } finally {
    dispatch(setIsLoadingProductListing(false));
  }
};

const toFilterCriteria = (groups) =>
  groups
    .map((group) =>
      group.selectedOptions
        .filter((val) =>
          group.asSlider
            ? group.selectedOptions.length == 2
            : val && val.length > 0
        )
        .map(
          (val) =>
            `${encodeURIComponent(group.id)}=${encodeURIComponent(val)}`
        )
    )
    .reduce((flat, current) => [...flat, ...current], []);

export const receiveProductFiltering = (productFiltering) => ({
  type: PRODUCT_FILTERING_RECEIVE,
  payload: productFiltering,
});

export const getProductFilteringAsQueryString = (
  productFilteringState,
  productListingState = null
) => {
  const currentSearchParams = new URLSearchParams(location.search);
  const searchParams = new URLSearchParams();

  // Keep certain params from the current query
  const paramsToKeep = ['page', 'q'];
  paramsToKeep.forEach(
    (key) =>
      currentSearchParams.has(key) &&
      searchParams.append(key, currentSearchParams.get(key))
  );

  // Loop each filter, add each selected option, checkboxes
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.selectedOptions.length > 0 &&
        f.filterGroupType != FilterGroupType.Range
    )
    .forEach((filter) =>
      filter.selectedOptions.forEach((selectedOption) => {
        searchParams.append(filter.id, selectedOption);
      })
    );

  // Range filters
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.filterGroupType == FilterGroupType.Range &&
        f.selectedOptions.length == 2
    )
    .forEach((filter) => {
      searchParams.append(
        `${filter.id}`,
        `${filter.selectedOptions[0]}-${filter.selectedOptions[1]}`
      );
    });

  // Sorting
  const selectedSorting = productFilteringState.sortCriteria?.sortItems?.find(
    (s) => s.selected
  );

  if (selectedSorting && selectedSorting.query) {
    searchParams.append('sort_by', selectedSorting.sortBy);
    searchParams.append('sort_direction', selectedSorting.sortDirection);
  }

  // Number to load
  if (productListingState?.productsPerLoadDropdownValue?.value) {
    searchParams.append(
      'products_per_load',
      productListingState.productsPerLoadDropdownValue.value
    );
  }

  return searchParams.toString();
};

export const onOptionChanged = (filterId, optionId) => (
  dispatch,
  getState
) => {
  const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
  const filterIndex = filters.findIndex((f) => f.id == filterId);
  if (filterIndex == -1) return;

  const { singleSelect, selectedOptions } = filters[filterIndex];
  const optionIndex = selectedOptions.indexOf(optionId);
  const isOptionSelected = optionIndex != -1;
  let updatedSelectedOptions = [];

  if (singleSelect) {
    updatedSelectedOptions = isOptionSelected ? [optionId] : [];
  } else {
    updatedSelectedOptions = isOptionSelected
      ? [
          // selected, remove it from the list
          ...selectedOptions.slice(0, optionIndex),
          ...selectedOptions.slice(optionIndex + 1),
        ]
      : [...selectedOptions, optionId]; // Not selected, add it to selected list
  }
  filters[filterIndex].selectedOptions = updatedSelectedOptions;

  dispatch(updateProductFilteringFilters(filters));
};

export const onRangeChanged = (filterId, values) => (
  dispatch,
  getState
) => {
  const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
  const filterIndex = filters.findIndex((f) => f.id == filterId);
  if (
    filterIndex == -1 ||
    filters[filterIndex].filterGroupType != FilterGroupType.Range
  )
    return;

  filters[filterIndex].selectedOptions = values;

  dispatch(updateProductFilteringFilters(filters));
};

const updateProductFilteringFilters = (filters) => ({
  type: PRODUCT_FILTERING_UPDATE_FILTERS,
  payload: filters,
});

export const onSortItemSelected = (sortItem) => (dispatch) => {
  dispatch({
    type: PRODUCT_FILTERING_SET_SORTING,
    payload: sortItem,
  });
  dispatch(queryProductFiltering());
};

export const clearAllFilters = () => (dispatch, getState) => {
  const productFilteringState = getState().productFiltering;
  const filtersCopy = Object.assign([], productFilteringState.filters);
  for (let filter of filtersCopy) {
    filter.selectedOptions = [];
  }
  dispatch(
    queryProductFiltering({
      ...productFilteringState,
      filters: filtersCopy,
    })
  );
};

export const clearFilterGroup = (filterGroupId) => (
  dispatch,
  getState
) => {
  const productFilteringState = getState().productFiltering;
  const filtersCopy = Object.assign([], productFilteringState.filters);
  const filterGroup = filtersCopy.find((f) => f.id == filterGroupId);
  if (!filterGroup) return;
  filterGroup.selectedOptions = [];
  dispatch(updateProductFilteringFilters(filtersCopy));
  dispatch(
    queryProductFiltering({
      ...productFilteringState,
      filters: filtersCopy,
    })
  );
};

// Groups filters by filter id. (Return format: {Color: ["Blå", "Grön"], Weight: [100]}})
const toFilterGroups = (filterArray) => {
  const reducedGroupedFilters = filterArray.reduce((result, filter) => {
    const filterAsKeyValue = filter.split('=');
    const filterName = decodeURIComponent(filterAsKeyValue[0]);
    const value = decodeURIComponent(filterAsKeyValue[1]);

    const prev = result[filterName] ?? [];
    return {
      ...result,
      [filterName]: [...prev, value].sort(),
    };
  }, {});

  return reducedGroupedFilters;
};

// Makes filters easily comparable to mcs filters (Return format: ["Color:Blå,Gul", "Weight:100"])
const toFilterGroupStrings = (reducedGroupedFilters, groups) => {
  const groupedFilters = Object.keys(reducedGroupedFilters).reduce(
    (acc, groupName) => {
      var sortedFilterGroup = reducedGroupedFilters[groupName].sort(
        (a, b) => b - a
      );

      const groupIsRangeType =
        groups.find((group) => group.id == groupName)?.filterGroupType ==
        'Range';

      if (groupIsRangeType) {
        if (sortedFilterGroup.length > 1) {
          const joinedRange = sortedFilterGroup.join('-');
          return [...acc, `${groupName}:${joinedRange}`];
        }
      } else {
        return [...acc, `${groupName}:${sortedFilterGroup.join(';')}`];
      }
    },
    []
  );

  return groupedFilters;
};
